import type { FlagDefinition } from './flag';

export type FlagsSnapshot = Readonly<Record<string, boolean>>;

export type FlagSource = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => FlagsSnapshot;
};

export type FlagsClientOptions = {
  source: FlagSource;
  overrides?: FlagsSnapshot;
};

export type FlagsClient = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => FlagsSnapshot;
  has: (flag: FlagDefinition) => boolean;
  get: (flag: FlagDefinition) => boolean | undefined;
  isEnabled: (flag: FlagDefinition) => boolean;
};

export function createFlagsClient(options: FlagsClientOptions): FlagsClient {
  const { source, overrides } = options;

  let lastSourceSnapshot: FlagsSnapshot | undefined;
  let mergedSnapshot: FlagsSnapshot | undefined;

  const getSnapshot = (): FlagsSnapshot => {
    const sourceSnapshot = source.getSnapshot();

    if (!overrides) {
      return sourceSnapshot;
    }

    // Memoize: only recompute when source snapshot ref changes
    if (sourceSnapshot !== lastSourceSnapshot) {
      lastSourceSnapshot = sourceSnapshot;
      mergedSnapshot = { ...sourceSnapshot, ...overrides };
    }

    return mergedSnapshot as FlagsSnapshot;
  };

  const client: FlagsClient = {
    subscribe: source.subscribe,
    getSnapshot,

    has(flag: FlagDefinition): boolean {
      return flag.key in getSnapshot();
    },

    get(flag: FlagDefinition): boolean | undefined {
      return getSnapshot()[flag.key];
    },

    isEnabled(flag: FlagDefinition): boolean {
      if (!client.has(flag)) {
        return flag.defaultValue;
      }
      return client.get(flag) === true;
    },
  };

  return client;
}
